import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, TriangleAlert } from 'lucide-react';

import type { SlaStatus } from '@/types/app';

/**
 * Live countdown to the first-contact deadline (#6).
 *
 * It ticks because the deadline is measured in minutes: a static "due in 4 minutes"
 * rendered once and left alone is wrong almost immediately, and this is the one
 * number on the screen a rep is supposed to act on.
 *
 * Only runs while the SLA is still pending. Once it is met or breached there is
 * nothing to count, so the interval is never created.
 */
export function SlaCountdown({ dueAt, status }: { dueAt: string | null; status: SlaStatus }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  const pending = status === 'pending' && dueAt !== null;

  useEffect(() => {
    if (!pending) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [pending]);

  if (!dueAt) return null;

  if (status === 'met') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
        <Clock className="size-3" aria-hidden="true" />
        {t('slaStatuses.met')}
      </span>
    );
  }

  if (status === 'breached') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
        <TriangleAlert className="size-3" aria-hidden="true" />
        {t('slaStatuses.breached')}
      </span>
    );
  }

  const remainingMs = new Date(dueAt).getTime() - now;
  const late = remainingMs <= 0;
  const total = Math.abs(Math.floor(remainingMs / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;

  // Latin digits and an explicit LTR direction: a mm:ss clock reversed by RTL
  // reads as the wrong time entirely.
  const clock = `${minutes}:${String(seconds).padStart(2, '0')}`;

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        late ? 'text-red-700' : minutes < 2 ? 'text-amber-700' : 'text-slate-600'
      }`}
      // The pulse marks it breached within two minutes; until then say "overdue"
      // rather than claiming a status the database has not set.
      title={late ? t('leads.slaOverdue') : t('leads.slaRemaining')}
    >
      <Clock className="size-3" aria-hidden="true" />
      <span dir="ltr">{late ? `-${clock}` : clock}</span>
    </span>
  );
}
